import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3">{children}</div>
  </div>
);

const SubSection = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-base font-semibold text-slate-700 mb-3">{title}</h3>
    <div className="text-slate-600 leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white font-sans selection:bg-blue-100 selection:text-blue-900">
      <Helmet>
        <title>Politique de confidentialité — M'EyeBus</title>
        <meta
          name="description"
          content="Politique de confidentialité de M'EyeBus : traitement des données personnelles des élèves, parents et chauffeurs conformément à la loi 09-08."
        />
      </Helmet>
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="M'EyeBus Logo" className="h-12 w-auto" />
            <img src="/logo_trademark.png" alt="M'EyeBus" className="h-6 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>
      </nav>

      {/* Page Content */}
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-6">
              <ShieldCheck className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-3">Politique de Confidentialité</h1>
            <p className="text-slate-500 text-sm">Dernière mise à jour : 24 mars 2026</p>
          </div>

          {/* Intro Banner */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-5 mb-12 text-slate-700">
            Bienvenue sur <strong>M'EyeBus</strong>. Cette politique de confidentialité vous informe sur la manière dont
            nous collectons, utilisons et protégeons vos informations personnelles lorsque vous utilisez notre site web
            et nos applications mobiles (iOS et Android) de suivi de bus scolaires.
          </div>

          {/* Engagement fort */}
          <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-5 mb-12 text-green-800 font-semibold">
            M'EyeBus ne collecte aucune donnée à des fins publicitaires ou commerciales. Aucune donnée n'est vendue,
            partagée ou transmise à des tiers à des fins de marketing ou de suivi publicitaire.
          </div>

          {/* Sections */}
          <Section title="1. Périmètre de cette politique">
            <p>Cette politique s'applique à l'ensemble des plateformes M'EyeBus :</p>
            <ul className="mt-3 space-y-2 list-none">
              {[
                {
                  title: 'Site web',
                  desc: "meyebus.ma — plateforme d'information et d'accès au tableau de bord administrateur.",
                },
                {
                  title: 'Application mobile parent',
                  desc: 'Application iOS et Android permettant aux parents de suivre le bus scolaire de leur enfant.',
                },
                {
                  title: 'Application mobile conducteur',
                  desc: 'Application iOS et Android utilisée par les conducteurs pour gérer les trajets.',
                },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500"></span>
                  <span>
                    <strong className="text-slate-700">{item.title}</strong> : {item.desc}
                  </span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="2. Données collectées par plateforme">
            <SubSection title="2.1 Site web (meyebus.ma)">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="font-semibold text-slate-700 mb-2">
                  Aucune donnée personnelle n'est collectée via le site web.
                </p>
                <p>
                  Le site web ne dépose aucun cookie de tracking, n'utilise aucun outil d'analyse comportementale et ne
                  collecte pas la localisation des visiteurs.
                </p>
              </div>
            </SubSection>

            <SubSection title="2.2 Application mobile — Parents (iOS & Android)">
              <p>
                L'application parent collecte les données suivantes lors de l'inscription et de l'utilisation du service
                :
              </p>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="text-left px-4 py-3 rounded-tl-lg">Type de donnée</th>
                      <th className="text-left px-4 py-3 rounded-tr-lg">Finalité</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-700">Nom et prénom</td>
                      <td className="px-4 py-3">Identification du compte parent</td>
                    </tr>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-700">Adresse e-mail</td>
                      <td className="px-4 py-3">Authentification et connexion au compte</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-700">Numéro de téléphone</td>
                      <td className="px-4 py-3">Contact et notifications liées au service</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-700">Identifiant utilisateur</td>
                      <td className="px-4 py-3">Gestion de session et sécurité du compte</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-4 text-amber-800">
                <strong>Localisation :</strong> L'application parent n'accède pas à la position GPS du parent. La carte
                affiche uniquement la position du bus scolaire, transmise par un GPS installé sur le bus.
              </div>
            </SubSection>

            <SubSection title="2.3 Application mobile — Conducteurs (iOS & Android)">
              <p>
                L'application conducteur collecte les mêmes données d'identification que l'app parent (nom, email,
                téléphone, identifiant).
              </p>
              <div className="mt-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl p-4 text-green-800">
                <p>
                  <strong>Données de localisation du conducteur :</strong> La fonctionnalité de transmission de la
                  position GPS du véhicule n'est pas encore activée dans la version actuelle de l'application. Cette
                  fonctionnalité sera implémentée dans une prochaine mise à jour, et cette politique sera mise à jour en
                  conséquence.
                </p>
              </div>
            </SubSection>
          </Section>

          <Section title="3. Services tiers utilisés">
            <p>
              M'EyeBus utilise les services tiers suivants, dont l'usage est strictement limité aux finalités décrites
              ci-dessous :
            </p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="text-left px-4 py-3 rounded-tl-lg">Service</th>
                    <th className="text-left px-4 py-3">Usage</th>
                    <th className="text-left px-4 py-3 rounded-tr-lg">Données transmises</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-700">Mapbox</td>
                    <td className="px-4 py-3">Affichage de la carte uniquement</td>
                    <td className="px-4 py-3">Aucune donnée personnelle transmise</td>
                  </tr>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">Firebase (Google)</td>
                    <td className="px-4 py-3">Envoi de notifications push uniquement</td>
                    <td className="px-4 py-3">Token d'appareil anonyme pour la livraison des notifications</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              Mapbox est utilisé exclusivement pour le rendu cartographique. Il ne collecte pas la position de
              l'utilisateur via notre application. Firebase Cloud Messaging est utilisé uniquement pour envoyer des
              notifications liées au service (départ du bus, arrivée, alertes).
            </p>
          </Section>

          <Section title="4. Absence de tracking et de publicité">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-4 font-semibold text-green-800">
              M'EyeBus ne fait aucun tracking publicitaire.
            </div>
            <p className="mt-4">Les données collectées ne sont jamais utilisées pour :</p>
            <ul className="mt-3 space-y-2 list-none">
              {[
                'Afficher des publicités ciblées',
                'Partager des données avec des régies publicitaires',
                'Vendre des données à des courtiers en données (data brokers)',
                'Suivre le comportement des utilisateurs à des fins commerciales',
                'Établir des profils marketing',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-red-400"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="5. Partage des informations">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-4 font-semibold text-green-800">
              Nous ne partageons pas vos données personnelles avec des tiers.
            </div>
            <p className="mt-4">
              Les données collectées sont uniquement accessibles aux utilisateurs autorisés dans le cadre du service
              M'EyeBus. Par exemple, un parent ne peut voir que les informations relatives au bus de son propre enfant.
            </p>
          </Section>

          <Section title="6. Sécurité des données">
            <p>
              Nous nous engageons à protéger la sécurité de vos informations. Nous mettons en œuvre des mesures de
              sécurité techniques et organisationnelles pour protéger vos données contre l'accès, la modification, la
              divulgation ou la destruction non autorisés. Toutes les communications entre l'application et nos serveurs
              sont chiffrées via HTTPS (TLS).
            </p>
          </Section>

          <Section title="7. Conservation des données">
            <p>
              Nous conservons vos informations personnelles aussi longtemps que nécessaire pour fournir les services
              demandés et pour nous conformer à nos obligations légales. Vous pouvez demander la suppression de vos
              données à tout moment en nous contactant.
            </p>
          </Section>

          <Section title="8. Vos droits">
            <p>
              Conformément à la réglementation en vigueur, vous disposez des droits suivants concernant vos données
              personnelles :
            </p>
            <ul className="mt-3 space-y-2 list-none">
              {[
                {
                  title: "Droit d'accès",
                  desc: 'Obtenir une copie de vos données personnelles.',
                },
                {
                  title: 'Droit de rectification',
                  desc: 'Corriger des données inexactes ou incomplètes.',
                },
                {
                  title: "Droit à l'effacement",
                  desc: 'Demander la suppression de vos données.',
                },
                {
                  title: "Droit d'opposition",
                  desc: 'Vous opposer au traitement de vos données.',
                },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500"></span>
                  <span>
                    <strong className="text-slate-700">{item.title}</strong> : {item.desc}
                  </span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="9. Modifications de cette politique">
            <p>
              Nous pouvons être amenés à mettre à jour cette politique de confidentialité, notamment lors de
              l'activation de nouvelles fonctionnalités (ex : transmission GPS du conducteur). Nous vous notifierons de
              toute modification en publiant la nouvelle politique sur cette page et en mettant à jour la date de
              "Dernière mise à jour" en haut du document.
            </p>
          </Section>

          <Section title="10. Nous contacter">
            <p>Si vous avez des questions concernant cette politique de confidentialité, veuillez nous contacter :</p>
            <div className="mt-4 bg-slate-50 rounded-xl p-5 border border-slate-200">
              <p className="font-semibold text-slate-800">M'EyeBus</p>
              <p className="mt-1">
                Email :{' '}
                <a href="mailto:contact@meyebus.ma" className="text-blue-600 hover:underline font-medium">
                  contact@meyebus.ma
                </a>
              </p>
            </div>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 py-12 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-1.5">
              <img src="/logo_trademark_corp.png" alt="M'EyeBus" className="h-6 w-auto object-contain" />
              <span className="text-sm text-[#0B2340] whitespace-nowrap">
                <span className="font-semibold">2026</span>, Tous droits réservés.
              </span>
            </div>
            <div className="flex gap-6">
              <Link
                to="/privacy-policy"
                className="text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm"
              >
                Confidentialité
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
